import { MapPin, AlertTriangle, ArrowLeft } from 'lucide-react';
import { DAY_OF_WEEK_LABELS, type DayOfWeek } from '@paulownia/shared/types';
import type { CurrentMarketInfo } from '@paulownia/shared/lib/storage';
import type { TenantMarketWithDetails } from './types';

interface MarketIncompatibleProps {
  tenantName: string;
  currentMarket: CurrentMarketInfo;
  compatibleMarkets: TenantMarketWithDetails[];
  onSelectMarket: (marketId: string, city: string, dayOfWeek: number) => void;
  onBackClick?: () => void;
}

/**
 * Shown when the merchant is not present at the current market.
 * Displays only compatible markets for selection.
 */
export function MarketIncompatible({
  tenantName,
  currentMarket,
  compatibleMarkets,
  onSelectMarket,
  onBackClick,
}: MarketIncompatibleProps) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="bg-amber-500 text-white p-6 pb-8 text-center relative">
        {onBackClick && (
          <button
            onClick={onBackClick}
            className="absolute left-3 top-1/2 -translate-y-1/2 p-2 hover:bg-white/20 rounded-lg transition-colors"
            title="Retour"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <AlertTriangle className="w-10 h-10 mx-auto mb-2" />
        <h1 className="text-xl font-bold">Marche incompatible</h1>
      </div>

      <div className="flex-1 max-w-lg mx-auto w-full px-4 py-6">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <p className="text-amber-800 text-center">
            <span className="font-semibold">{tenantName}</span> n'est pas present a{' '}
            <span className="font-semibold">
              {currentMarket.city} — {DAY_OF_WEEK_LABELS[currentMarket.dayOfWeek as DayOfWeek]}
            </span>.
          </p>
        </div>

        <p className="text-sm text-gray-600 mb-4 text-center">
          Choisissez un marche compatible :
        </p>

        <div className="space-y-3">
          {compatibleMarkets.map((tm) => (
            <button
              key={tm.marketId}
              onClick={() => onSelectMarket(tm.marketId, tm.market.city, tm.market.dayOfWeek)}
              className="w-full bg-white rounded-xl shadow-md p-4 text-left hover:shadow-lg transition-shadow active:scale-[0.98]"
            >
              <div className="flex items-center gap-3">
                <div className="bg-orange-100 rounded-full p-2 flex-shrink-0">
                  <MapPin className="w-5 h-5 text-orange-600" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-gray-900">{tm.market.city}</div>
                  <div className="text-sm text-gray-500">
                    {DAY_OF_WEEK_LABELS[tm.market.dayOfWeek as DayOfWeek]} — {tm.market.openingTime} - {tm.market.closingTime}
                  </div>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
